import OlVectorLayer from 'ol/layer/Vector';
import OlVectorSource from 'ol/source/Vector';
import OlDraw from 'ol/interaction/Draw';
import olModify from 'ol/interaction/Modify';
import * as olCondition from 'ol/events/condition';
import olSnap from 'ol/interaction/Snap';
import * as olExtent from 'ol/extent';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import OlCircle from 'ol/geom/Circle';
import { fromCircle as OlPolygonFromCircle } from 'ol/geom/Polygon';

import {
    INTERACTION_TYPE,
    DRAW_MODUS,
    DEFAULT_DRAW_STYLE,
    DEFAULT_MODIFY_STYLE,
    DEFAULTPOLYGON_DRAW_STYLE,
    DEFAULTLINE_DRAW_STYLE,
    DEFAULT_FEATURE_STYLE_SETTINGS
} from '../../../constants';

export class DrawOnMap {
    constructor(map) {
        this.map = map;
        this.vectorSource = new OlVectorSource({ wrapX: false });
        this.snapSource = new OlVectorSource();
        const previewStyle = DEFAULT_DRAW_STYLE(DEFAULT_FEATURE_STYLE_SETTINGS.pointSizeSelected);
        this.vector = new OlVectorLayer({
            source: this.vectorSource,
            style: previewStyle

        });
        const snapLayer = new OlVectorLayer({
            source: this.snapSource,
            style: null
        });
        this.map.addLayer(snapLayer);
        this.vector.setZIndex(999);
    }

    setFeature(feature) {
        this.feature = feature;
    }

    start(interactionType, drawObject ) {
        this.interactionType = interactionType;
        this.drawObject = drawObject || {};
        this.map.addLayer(this.vector);
        this.map.getViewport().style.cursor = 'crosshair';
        this.modify = new olModify({
            source: this.vectorSource,
            condition: this.canAddPoint.bind(this),
            insertVertexCondition: this.canEditPoint.bind(this),
            deleteCondition: this.canDeletePoint.bind(this),
        });
        this.snap = new olSnap({ source: this.snapSource });
    }

    draw(autoFinish = true) {
        this.drawObject.mode = DRAW_MODUS.Draw;
        this.autoFinish = autoFinish;
        this.drawPromise = new Promise((resolve, reject) => {
            this.resolvePromise = resolve;
            this.rejectPromise = reject;
        });
        this.drawing = this.getDrawVector(this.interactionType);
        this.drawing.on('drawstart', event => {
            this.drawStart(event);
            event.feature.getGeometry().on('change', this.drawChange.bind(this));
        });
        this.drawing.on('drawend', event => {
            this.onDrawEnd(event);
        });
        this.map.addInteraction(this.drawing);
        this.map.addInteraction(this.snap);
        return this.drawPromise;
    }

    finishDrawing() {
        this.drawing.finishDrawing();
    }

    onDrawEnd(event) {
        event && this.drawChange(event);
        this.map.removeInteraction(this.drawing);
        if (this.autoFinish) {
            this.drawEnd(event);
            return;
        }
        this.modifyDraw();
    }

    modifyDraw() {
        this.drawObject.mode = DRAW_MODUS.Modify;
        this.map.addInteraction(this.modify);
        this.map.addInteraction(this.snap);
    }

    setSnap(snapPoints) {
        this.snapPoints = snapPoints;
        const features = this.snapPoints.map(point => new Feature({ geometry: new Point(point), }));
        this.snapSource.clear();
        this.snapSource.addFeatures(features);
    }

    setDeleteMode(deleteMode) {
        this.deleteMode = deleteMode;
    }

    setRadius(radius) {
        this.radius = radius;
    }

    appendCoordinates(coordinates) {
        if (this.drawing){
            this.drawing.appendCoordinates(coordinates);
        }
    }

    removeLastpoint() {
        if (this.drawing){
            this.drawing.removeLastPoint();
        }
    }

    end() {
        this.map.getViewport().style.cursor = '';
        this.map.removeInteraction(this.drawing);
        this.map.removeInteraction(this.snap);
        this.map.removeInteraction(this.modify);
        this.map.removeLayer(this.vector);
    }

    drawChange(event) {
        const geometry = event.type === 'drawend' ? event.feature.getGeometry() : event.target;
        if (this.interactionType === INTERACTION_TYPE.Polygon) {
            this.drawObject.polygon = geometry.clone();
            this.drawObject.coordinates = geometry.getCoordinates()[0];
        } else if (this.interactionType === INTERACTION_TYPE.Point) {
            const center = olExtent.getCenter(geometry.getExtent());
            this.drawObject.coordinates = center;
        } else {
            this.drawObject.polygon = geometry.clone();
            this.drawObject.coordinates = geometry.getCoordinates();
        }
    }

    drawStart(event) {
        event.feature.setStyle(() => {
            // Return value
            const styles = [];
            // Default style
            if (this.drawObject.mode === DRAW_MODUS.Modify) {
                styles.push(DEFAULT_MODIFY_STYLE(DEFAULT_FEATURE_STYLE_SETTINGS.pointSizeSelected));
            } else {
                styles.push(DEFAULT_DRAW_STYLE(DEFAULT_FEATURE_STYLE_SETTINGS.pointSizeSelected));
            }
            // Add circle for polygon
            if (this.interactionType === INTERACTION_TYPE.Polygon) {
                styles.push(DEFAULTPOLYGON_DRAW_STYLE);
            } else if (this.interactionType === INTERACTION_TYPE.LineString) {
                styles.push(DEFAULTLINE_DRAW_STYLE);
            }
            return styles;
        });
        // Clear features
        this.vector.getSource().clear();
    }

    getDrawVector(interactionType) {
        if (this.interactionType === INTERACTION_TYPE.Circle) {
            return new OlDraw({
                source: this.vectorSource,
                type: INTERACTION_TYPE.Point,
                geometryFunction: (coordinates) => {
                    return OlPolygonFromCircle(new OlCircle(coordinates, parseInt(this.radius)));
                },
                condition: this.canAddPoint.bind(this),
                style: []
            });
        }

        return new OlDraw({
            source: this.vectorSource,
            type: interactionType,
            condition: this.canAddPoint.bind(this),
            style: this.vector.getStyle()
        });
    }

    drawEnd(event) {
        this.resolvePromise(event);
        this.end();
    }

    canAddPoint(event) {
        return olCondition.primaryAction(event) ;
    }

    canEditPoint(event) {
        return olCondition.primaryAction(event) && !this.deleteMode;
    }

    canDeletePoint(event) {
        return olCondition.primaryAction(event) && olCondition.singleClick(event) && this.deleteMode;
    }
}
